// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Fiscal receipt modal (NF525 compliant)
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/services/api-client';
import { useAppStore } from '@/store/app-store';

interface ReceiptData {
  // Company
  company?: {
    company_name?: string;
    address?: string;
    siret?: string;
    vat_number?: string;
  };
  // Transaction identification
  transaction_id?: number | string;
  transaction_ref?: string;
  fiscal_sequence?: string;
  date?: string;
  date_formatted?: string;
  synced_at?: string;
  employee_name?: string;
  // Items
  items?: Array<{
    product_name?: string;
    name?: string;
    quantity: number;
    unit_price_ttc?: number;
    unit_price_ht?: number;
    unit_price?: number;
    total_ttc?: number;
    vat_rate?: number;
    vat_amount?: number;
  }>;
  // Totals
  total_ht?: number;
  total_tva?: number;
  total_ttc?: number;
  total_amount?: number;
  // VAT breakdown
  vat_breakdown?: Record<string, { ht?: number; tva?: number; ttc?: number }>;
  // Payment
  payment_method?: string;
  payment_reference?: string;
  // Fiscal
  fiscal_hash?: string;
  fiscal_hash_short?: string;
  nf525_mention?: string;
  // Flags
  is_duplicate?: boolean;
  duplicate_number?: number;
  duplicate_mention?: string;
  is_cancellation?: boolean;
  cancellation_of_id?: number | string;
  cancellation_reason?: string;
}

interface ReceiptModalProps {
  transactionId: number | string | null;
  isDuplicate?: boolean;
  onClose: () => void;
}

const PAYMENT_LABELS: Record<string, string> = {
  CB: 'Carte Bancaire',
  card: 'Carte Bancaire',
  ESPECES: 'Especes',
  cash: 'Especes',
  PREVENTE: 'Prevente',
  voucher: 'Bon / Prevente',
  cheque: 'Cheque',
  ticket_restaurant: 'Ticket Restaurant',
};

function fmtAmount(amount: number | undefined | null): string {
  const num = parseFloat(String(amount)) || 0;
  return num.toFixed(2) + '\u20AC';
}

function fmtDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export default function ReceiptModal({ transactionId, isDuplicate, onClose }: ReceiptModalProps) {
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showToast = useAppStore((s) => s.showToast);

  useEffect(() => {
    if (!transactionId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchReceipt = async () => {
      try {
        let data: ReceiptData;
        if (isDuplicate) {
          data = (await apiClient.getDuplicateReceipt(transactionId)) as unknown as ReceiptData;
        } else {
          data = (await apiClient.getReceipt(transactionId)) as unknown as ReceiptData;
        }
        if (!cancelled) setReceipt(data);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message || 'Erreur chargement ticket fiscal');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReceipt();
    return () => { cancelled = true; };
  }, [transactionId, isDuplicate]);

  if (!transactionId) return null;

  const handlePrint = () => {
    if (!receipt) return;
    const receiptEl = document.getElementById('fiscal-receipt-content');
    if (!receiptEl) return;

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showToast('Impossible d\'ouvrir la fenetre d\'impression', 'error');
        return;
      }
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="fr">
        <head>
          <meta charset="UTF-8">
          <title>Ticket Fiscal</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.3; width: 80mm; padding: 4mm; color: #000; }
            .r-sep { border: none; border-top: 1px dashed #000; margin: 4px 0; }
            .r-sep-bold { border: none; border-top: 2px solid #000; margin: 4px 0; }
            .r-center { text-align: center; }
            .r-row { display: flex; justify-content: space-between; }
            .r-bold { font-weight: bold; }
            .r-grand { font-weight: bold; font-size: 14px; border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 4px 0; }
            table { width: 100%; font-size: 10px; border-collapse: collapse; }
            th, td { text-align: right; padding: 1px 2px; }
            th:first-child, td:first-child { text-align: left; }
            th { border-bottom: 1px solid #000; }
            @media print { body { width: auto; padding: 0; } }
          </style>
        </head>
        <body>${receiptEl.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
        printWindow.onafterprint = () => printWindow.close();
      };
      showToast('Impression lancee', 'success');
    } catch {
      showToast('Erreur impression', 'error');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/70 animate-fade-in">
      <div className="bg-surface rounded-xl w-full max-w-[400px] max-h-[90vh] flex flex-col overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white">Ticket de caisse</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin mr-2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Chargement...
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-danger text-sm mb-2">{error}</p>
              <p className="text-gray-500 text-xs">
                Le ticket fiscal sera disponible apres synchronisation.
              </p>
            </div>
          )}

          {receipt && !loading && !error && (
            <div
              id="fiscal-receipt-content"
              className="bg-white text-black font-mono text-[13px] leading-snug p-4 rounded"
            >
              {/* Duplicate banner */}
              {receipt.is_duplicate && (
                <div className="text-center font-bold bg-gray-100 border border-gray-400 p-1.5 mb-2 text-xs">
                  DUPLICATA N&deg;{receipt.duplicate_number ?? '?'}
                  {receipt.duplicate_mention && <><br />{receipt.duplicate_mention}</>}
                </div>
              )}

              {/* Cancellation banner */}
              {receipt.is_cancellation && (
                <div className="text-center font-bold bg-red-50 border border-red-600 text-red-600 p-1.5 mb-2 text-xs">
                  *** ANNULATION ***<br />
                  Ticket original #{receipt.cancellation_of_id ?? '?'}<br />
                  Motif: {receipt.cancellation_reason ?? 'Non specifie'}
                </div>
              )}

              {/* Company header */}
              <div className="text-center mb-2">
                <div className="font-bold text-base">{receipt.company?.company_name ?? 'SIDEVIEW'}</div>
                <div className="text-[11px] text-gray-600">
                  {receipt.company?.address ?? ''}<br />
                  SIRET: {receipt.company?.siret ?? 'N/A'}
                  {receipt.company?.vat_number && <><br />TVA: {receipt.company.vat_number}</>}
                </div>
              </div>

              <hr className="border-t-2 border-black my-2" />

              {/* Transaction ID + date */}
              <div className="flex justify-between text-xs">
                <span>Ticket N&deg;</span>
                <span className="font-bold">
                  {receipt.fiscal_sequence ?? receipt.transaction_ref ?? receipt.transaction_id ?? 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Date:</span>
                <span>{receipt.date_formatted ?? fmtDate(receipt.date)}</span>
              </div>
              {receipt.employee_name && (
                <div className="flex justify-between text-xs">
                  <span>Caissier:</span>
                  <span>{receipt.employee_name}</span>
                </div>
              )}

              <hr className="border-t border-dashed border-gray-400 my-2" />

              {/* Items */}
              <div className="flex justify-between font-bold text-xs pb-1 border-b border-black">
                <span>Article</span>
                <span>Montant</span>
              </div>
              {(receipt.items ?? []).map((item, idx) => (
                <div key={idx} className="py-1">
                  <div className="font-medium text-xs">{item.product_name ?? item.name ?? 'Article'}</div>
                  <div className="flex justify-between text-xs pl-2">
                    <span>
                      {item.quantity} x {fmtAmount(item.unit_price_ttc ?? item.unit_price)}
                    </span>
                    <span>
                      {fmtAmount(
                        item.total_ttc != null
                          ? item.total_ttc
                          : item.quantity * (item.unit_price_ttc ?? item.unit_price ?? 0),
                      )}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-500 pl-2">
                    HT: {fmtAmount(item.unit_price_ht)} | TVA {item.vat_rate ?? 0}%: {fmtAmount(item.vat_amount)}
                  </div>
                </div>
              ))}

              <hr className="border-t border-dashed border-gray-400 my-2" />

              {/* Totals */}
              <div className="flex justify-between text-xs py-0.5">
                <span>Total HT:</span>
                <span>{fmtAmount(receipt.total_ht)}</span>
              </div>
              <div className="flex justify-between text-xs py-0.5">
                <span>Total TVA:</span>
                <span>{fmtAmount(receipt.total_tva)}</span>
              </div>
              <div className="flex justify-between font-bold text-base border-y-2 border-black py-1.5 my-1">
                <span>TOTAL TTC:</span>
                <span>{fmtAmount(receipt.total_ttc ?? receipt.total_amount)}</span>
              </div>

              {/* VAT breakdown */}
              {receipt.vat_breakdown && Object.keys(receipt.vat_breakdown).length > 0 && (
                <>
                  <table className="my-2">
                    <thead>
                      <tr>
                        <th className="text-left">Taux</th>
                        <th>HT</th>
                        <th>TVA</th>
                        <th>TTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(receipt.vat_breakdown)
                        .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                        .map(([rate, amounts]) => (
                          <tr key={rate}>
                            <td className="text-left">TVA {rate}%</td>
                            <td>{fmtAmount(amounts.ht)}</td>
                            <td>{fmtAmount(amounts.tva)}</td>
                            <td>{fmtAmount(amounts.ttc)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  <hr className="border-t border-dashed border-gray-400 my-2" />
                </>
              )}

              {/* Payment */}
              {receipt.payment_method && (
                <div className="py-1">
                  <div className="flex justify-between text-xs">
                    <span>Paiement:</span>
                    <span>{PAYMENT_LABELS[receipt.payment_method] ?? receipt.payment_method}</span>
                  </div>
                  {receipt.payment_reference && (
                    <div className="flex justify-between text-[11px]">
                      <span>Ref:</span>
                      <span>{receipt.payment_reference}</span>
                    </div>
                  )}
                </div>
              )}

              <hr className="border-t-2 border-black my-2" />

              {/* NF525 fiscal */}
              <div className="text-center text-[11px] mt-2 pt-2">
                <div className="font-bold tracking-wider font-mono">
                  Signature: {receipt.fiscal_hash_short ?? receipt.fiscal_hash?.substring(0, 8) ?? 'N/A'}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {receipt.nf525_mention ?? 'Logiciel certifie NF525'}
                </div>
                <div className="text-[10px] text-gray-500">SIDEVIEW NF525 v1.0</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-4 py-3 border-t border-white/10">
          <button type="button" onClick={onClose} className="btn-secondary text-xs py-2 flex-1">
            Fermer
          </button>
          {receipt && (
            <button type="button" onClick={handlePrint} className="btn-primary text-xs py-2 flex-1 flex items-center justify-center gap-1.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Imprimer
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
